import React, { useContext, useEffect } from 'react';
import Layout from '@/components/layout';
import PaymentOption from './_payment-options';
import { Helmet } from 'react-helmet';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import { Link } from '@latitude/link';
import { PageContext } from '@/context/PageContext'; // Ensure this import is correct
import PromoBanner from '@/components/PromoBannerRebrand/PromoBannerRebrand';
import FeatureCardList from '@/components/FeatureCardListRebrand/FeatureCardListRebrand';
import { CARD_CODE, CARD_DATA } from '@/data/pages/credit-cards/card-offer';
import { AnalyticsContext } from 'latitude-analytics';
import LabRatesAndFeesColumn from '@/components/LabRatesAndFeesColumnRebrand';
import Button from '@latitude/button-rebrand';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import FeatureGridV1 from '@/components/FeatureGridV1/FeatureGridV1';
import { getFeatureGridV1DataFromContentful } from '@/components/FeatureGridV1/contentfulFeatureGridV1';
import { RewardsCarasoule } from '@/components/RewardsCarasoule/RewardsCarasoule';
import { getRewardsCarouselDataFromContentful } from '@/components/RewardsCarasoule/contentfulRewardsCarousel';
import StickyNavigation from '@/components/StickyNavigationRebrand/StickyNavigation';
import HowToApply from '@/components/HowToApplyRebrand/HowToApplyRebrand';
import GemMicrositeHeader from './_gem-microsite-header-rebrand';
import SvgInline from '@latitude/svg-inline';
import PromoBannerCarousel from '@/components/PromoBannerRebrandCarousel/PromoBannerCarouselRebrand';
import HeroBranded from '@/components/HeroBrandedRebrand/HeroBrandedRebrand';
import IFExplainer from '@/components/lab-components/IFExplainerRebrand';
import { getIFExplainerDataFromContentful } from '@/components/lab-components/IFExplainerRebrand/contentfulIFExplainer';
import pageDataBackup from './data/gem-visa-page-backup.json';
import StandaloneText from '@/components/lab-components/StandaloneText';
import { getFeatureCardListRebrandDataFromContentful } from '@/components/FeatureCardListRebrand/contentfulFeatureCardListRebrand';

const PAGE_NAVIGATION = [
  {
    anchor: 'benefits',
    label: 'Benefits'
  },
    {
    anchor: 'rewards',
    label: 'Rewards'
  },
  {
    anchor: 'rates',
    label: 'Rates & Fees'
  },
  {
    anchor: 'payment-options',
    label: 'Payment options'
  },
  {
    anchor: 'how-to-apply',
    label: 'How to apply'
  }
];

const FEATURE_GRID_DATA = {
  features: [
    {
      icon: <SvgInline name="icon-confetti-rebrand" />,
      headingText: 'Earn ongoing rewards',
      paragraphText: (
        <>
          Spend $1000 or more on general purchases in a statement period and
          <strong> get 3% back in Latitude Rewards</strong> when you spend on
          groceries, dining, food delivery, theatre and cinema ticket purchases.
          Turn your Latitude Rewards into eGift cards, experiences and more.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="https://assets.latitudefinancial.com/legals/terms-conditions/gemvisa-rewards.pdf"
            trackEventData={{
              label: 'tc-applys',
              location: 'earn-ongoing-rewards'
            }}
          >
            T&Cs apply.
          </Link>
        </>
      )
    },
    {
      icon: <SvgInline name="icon-shopping-cart-simple-rebrand" />,
      headingText: '0% interest on $250+ purchases',
      paragraphText: (
        <>
          Enjoy 0% interest for 6 months on general purchases of $250 or more.
          Select your purchases in the Latitude App or Latitude Service Centre
          within 30 days.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="https://latitudefs.zendesk.com/hc/en-au/articles/4409706884113-Your-Guide-to-Receive-6-Months-Interest-Free-on-250-Purchases-within-30-Days"
            trackEventData={{
              label: 'heres-how',
              location: '0-interest-on-250-plus-purchase'
            }}
          >
            Here’s how.
          </Link>{' '}
          For all other purchases, get up to 55 days interest free.<sup>1</sup>
        </>
      )
    },
    {
      icon: <SvgInline name="icon-calender-rebrand" />,
      headingText: '0% interest offers from 6 – 60 months',
      paragraphText: (
        <>
          Enjoy more time to pay with{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/interest-free/find-an-offer/?gemid2=lfs-web"
            trackEventData={{
              label: '0-interst-plans',
              location: '0-interest-offers-from-6-60-months'
            }}
          >
            0% interest plans
          </Link>
            at{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/interest-free/find-a-store/?gemid2=lfs-web"
            trackEventData={{
              label: '100s-of-retailers',
              location: '0-interest-offers-from-6-60-months'
            }}
          >
            100s of retailers 
          </Link>
           online and in-store.<sup>3</sup> No penalties if you pay your plan
          earlier.{' '}
        </>
      )
    },
    {
      icon: <SvgInline name="icon-users-rebrand" />,
      headingText: '$0 additional card holder fee',
      paragraphText:
        'Share all the features and benefits without needing to open a second account.',
      learnMoreUrl:
        'https://latitudefs.zendesk.com/hc/en-au/articles/36591875170577-Additional-Cardholders?gemid2=lfs-web'
    }
  ],
  moreFeatureTitle: 'View all benefits',

  moreFeatures: [
    {
      title: `Everyday offers and discounts `,
      content: (
        <>
          Get cashback in Latitude Rewards when you shop the very latest
          Merchant Offers in our 
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/mobile-app/?gemid2=lfs-web"
            trackEventData={{
              label: 'latitude-app',
              location: 'everyday-offers-and-discounts'
            }}
          >
            Latitude App.
          </Link>
        </>
      )
    },
    {
      title: `Digital wallet compatible `,
      content:
        'Leave your wallet at home with Apple Pay and Google Pay integration for easy, secure payments.'
    },
    {
      title: `Enjoy more time to pay `,
      content: (
        <>
          Shop smart with long term 
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/interest-free/?gemid2=lfs-web"
            trackEventData={{
              label: 'interest-free-payment-plans',
              location: 'enjoy-more-time-to-pay'
            }}
          >
            Interest Free payment plans
          </Link>
           using minimum or equal monthly payments. No penalties if you pay your
          plan early.
        </>
      )
    },
    {
      title: `Top-rated app to complement your card`,
      content: (
        <>
          Stay on top of payments with the 4.8/5 rated {' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/mobile-app/?gemid2=lfs-web"
            trackEventData={{
              label: 'latitude-app',
              location: 'everyday-offers-and-discounts'
            }}
          >
            Latitude App
          </Link>
           and never miss a due date.  
        </>
      )
    },
    {
      title: `Save with Expedia and Wotif`,
      content: (
        <>
          Get 10% off on selected hotels with Expedia and Wotif<sup>4</sup>.
          Exclusions and T&Cs Apply. {' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/travelpartners/?gemid2=lfs-web"
            trackEventData={{
              label: 'book-now',
              location: 'save-with-expedia-and-wotif'
            }}
          >
            Book Now
          </Link>
           
        </>
      )
    },
    {
      title: `Around the clock protection`,
      content: (
        <>
          Stay safe with around-the-clock fraud protection. 
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/scams-hub/?gemid2=lfs-web"
            trackEventData={{
              label: 'here-is-how-we-protect-you',
              location: 'around-the-clock-protection'
            }}
          >
            Here’s how we protect you. 
          </Link>
           
        </>
      )
    }
  ]
};

const HOW_TO_APPLY_DATA = {
  id: 'how-to-apply',
  howToApply: {
    sectionTitle: 'How To apply',
    items: [
      {
        title: 'Apply online',
        description: 'You can check your eligibility and apply in 10 minutes.'
      },
      {
        title: 'Wait a minute',
        description:
          '60 seconds is all it takes for us to respond to your application.'
      },
      {
        title: 'Ready, set, shop',
        description:
          'Set up your card in your digital wallet and start shopping today!'
      }
    ]
  },
  eligibility: {
    sectionTitle: 'Eligibility',
    items: [
      {
        title: 'You must:',
        list: [
          'Be 18 years or over',
          'A resident of Australia or a temporary resident who legally has the right to work in Australia (and intend to stay) for at least the next 12 months.'
        ]
      },
      {
        title: 'Able to supply:',
        list: [
          "Your driver's licence, passport or Medicare card",
          'Details of your income, living expenses & debts'
        ]
      }
    ]
  }
};

const IF_EXPLAINER_DATA = {
  title: 'Pay over time with Latitude Interest Free payment plans',
  description:
    'There are several ways to pay when you choose a long-term Interest Free payment plan at 100s of retailer partners. Flexible plan lengths might differ by store.',
  tiles: [
    {
      id: 1,
      tagText: 'Always Equal',
      title: 'Equal Monthly plan',
      description: [
        'Keep it simple and pay the same amount every month.',
        'Total plan amount is paid in the agreed number of monthly payments. ',
        'Gives you flexibility to pay out early without any penalty.'
      ]
    },
    {
      id: 2,
      tagText: 'Pay extra anytime',
      title: 'Minimum Monthly plan',
      description: [
        'Available provided you pay your credit card minimum payment each month.',
        'Gives you flexibility to add extra when possible.',
        'Paying this way may not clear the plan within your plan period.',
        'Interest applies on any outstanding balance at the end of your plan period.'
      ]
    },
    {
      id: 3,
      tagText: 'Pay anytime',
      title: 'Deferred Payment plan',
      description: [
        'No need to pay anything upfront.',
        'Make payments at any time it suits you during plan period.',
        'Interest applies on any outstanding balance at the end of your plan period.'
      ]
    }
  ]
}; 

// Card data array
const cardData = CARD_DATA.filter(card => card.code !== CARD_CODE.GEM);

function CreditCardsPage(props) {
  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || pageDataBackup;
  const featureGridData =
    getFeatureGridV1DataFromContentful(state?.featureSliderData?.[0]) ||
    FEATURE_GRID_DATA;
  const rewardsCarouselData =
    getRewardsCarouselDataFromContentful(state?.rewardsCarouselData?.[0]) ||
    null;
  const featureCardListData = getFeatureCardListRebrandDataFromContentful(
    state?.featureCardListRebrandData?.[0]
  ) || { cardList: cardData };
  const ifExplainerData =
    getIFExplainerDataFromContentful(state?.ifExplainerCollectionData?.[0]) ||
    IF_EXPLAINER_DATA;
  /** - END - */
  const [analytics] = useContext(AnalyticsContext);
  useEffect(() => {
    const sourceCode = localStorage.getItem('source_code') || 'IDAAA';
    // adding pageData to analytics
    analytics.pageData = {
      page_pageTitle: 'gem-visa',
      page_pageType: 'product-details-page',
      page_siteSection: 'cards',
      page_siteSubsection: 'gem-visa',
      product_productid: ['CCAUGEM'],
      application_sourcecode: sourceCode || 'IDAAA'
    };
  }, []);

  return (
      <div
      css={`
        .top-menu-items {
          visibility: hidden;
        }
        .logon-button-wrapper {
          visibility: hidden;
        }
        .search-button {
          visibility: hidden !important;
        }
        .hamburger-button {
          display: none !important;
        }
      `}
    > 
    <Layout location={props.location} 
    noIndex
    title="Finder Exclusive | Latitude Gem Visa"
    >
      <main>
        <HeroV2
          {...(state?.heroBannerData?.[0] ||
            pageDataBackup?.heroBannerData?.[0])}
        />

        <StickyNavigation
          items={PAGE_NAVIGATION}
          enableAnchorUpdate={true}
          trackId="sticky-get-started"
          stickyOffset={80}
        />

        <FeatureGridV1
          id="benefits"
          trackingId="lowrate"
          {...featureGridData}
        />

        <RewardsCarasoule
          id="rewards"
          {...(rewardsCarouselData || {
            title: 'Experience more with Latitude Rewards',
            subtitle:
              'Each Latitude Reward is worth $1 towards dining and cinema experiences, eGift cards, and more - no points conversion needed.',
            rewards: [
              {
                imageUrl: '/images/credit-cards/gem-visa/rewards-1.webp',
                title: 'Earn on eligible spend',
                description: (
                  <span>
                    Get 3% back in Latitude Rewards<sup>2</sup> on eligible
                    spend across groceries, dining, food delivery, movie and
                    theatre ticket purchases after you spend $1,000 or more on
                    general purchases in a statement period.{' '}
                    <Link
                      noStyle
                      style={{ textDecoration: 'underline' }}
                      href="https://assets.latitudefinancial.com/legals/terms-conditions/gemvisa-rewards.pdf"
                      trackEventData={{
                        location:
                          'experience-more-with-latitude-rewards' ||
                          'rewards-carousel'
                      }}
                    >
                      T&Cs apply
                    </Link>
                    .
                  </span>
                )
              },
              {
                imageUrl: '/images/credit-cards/gem-visa/rewards-2.webp',
                title: 'Browse tons of rewards',
                description: (
                  <>
                    Redeem your Latitude Rewards<sup>2</sup> for dining and
                    cinema vouchers, eGift cards, and more in your Latitude App.
                    Enjoy your rewards your way.
                  </>
                )
              },
              {
                imageUrl: '/images/credit-cards/gem-visa/rewards-3.webp',
                title: 'Redeem in seconds',
                description: (
                  <span>
                    Redeem your rewards in seconds. Just use the Latitude app or{' '}
                    <Link
                      noStyle
                      style={{ textDecoration: 'underline' }}
                      href="https://servicecentre.latitudefinancial.com.au/login?migration=true"
                    >
                      log in
                    </Link>{' '}
                    to the Latitude Service Centre.
                  </span>
                )
              }
            ],
            rewardsCta: (
              <Button
                className={`rewards-carousel-cta`}
                variant="secondary-blue"
                size="lg"
                ctaText="Learn More"
                ctaUrl="/credit-cards/gem-visa/latitude-rewards/"
                trackEventData={{
                  category: 'text-link',
                  location: 'experience-more-with-latitude-rewards',
                  label: 'learn-more'
                }}
                trackId="rewards-carousel"
              />
            )
          })}
        />

        {state?.standaloneTextData?.[0] && (
          <StandaloneText {...state?.standaloneTextData?.[0]} />
        )}

        <LabRatesAndFeesColumn isRate={true} {...state.ratesAndFeesData[0]} />

        <PaymentOption />

        <HowToApply data={HOW_TO_APPLY_DATA} />

        <PromoBannerCarousel {...state?.promoBannerData[0]} />
        <HeroBranded heading={<>Download the Latitude credit card app</>} />

        <FeatureCardList {...featureCardListData} />
        <IFExplainer {...ifExplainerData} />
        <ImportantInformation
          id="important-Info"
          css={`
            overflow-x: hidden;
            .row {
              @media (min-width: 1600px) {
                padding-left: ${PADDING.P32};
                padding-right: ${PADDING.P32};
              }
            }
          `}
          data={require('../../../data/pages/credit-cards/latitude-low-rate-mastercard.json')}
          {...{
            ...state?.importantInfoData?.[0],
            sectionOneColummClasses: 'col-12 col-lg-6',
            sectionTwoColummClasses: 'col-12 col-lg-6'
          }}
        />
      </main>
    </Layout>
    </div>
  );
}

export default CreditCardsPage;
